import { useCallback, useEffect, useMemo, useState } from 'react';
import { getCashFlow, getSpendingTree, type CashFlow as Flow, type CategorySpend, type MerchantSpend } from '../api.js';
import CashFlowSankey, { type FlowDrill } from '../components/CashFlowSankey.js';
import { Card, EmptyState, ErrorState, Group, MoreRow, Row, Segmented } from '../ui/index.js';
import { dollars, isUncategorized, money } from './format.js';
import { RANGES, rangeOf, SkeletonGroup, type RangeKey } from './parts.js';
import { useCategoryTree } from './tree.js';
import CategoryDrill from './CategoryDrill.js';

type Depth = '1' | '2';
const DEPTHS: readonly { value: Depth; label: string }[] = [
  { value: '1', label: 'Top-level' },
  { value: '2', label: 'Detailed' },
];

/**
 * Cash flow — one number first ("Spent this month"), the Sankey of where it
 * came from and went, then the categories (top-level or one level down) and
 * the merchants behind them.
 */
export default function CashFlow(): JSX.Element {
  const cats = useCategoryTree();
  const [rangeKey, setRangeKey] = useState<RangeKey>('month');
  const [depth, setDepth] = useState<Depth>('1');
  const range = useMemo(() => rangeOf(rangeKey), [rangeKey]);
  const [flow, setFlow] = useState<Flow | null>(null);
  const [byCategory, setByCategory] = useState<CategorySpend[]>([]);
  const [byMerchant, setByMerchant] = useState<MerchantSpend[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allCats, setAllCats] = useState(false);
  const [allMerchants, setAllMerchants] = useState(false);
  const [drill, setDrill] = useState<FlowDrill | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [f, spend] = await Promise.all([
        getCashFlow(range.from, range.to),
        getSpendingTree(range.from, range.to, { depth: Number(depth) }),
      ]);
      setFlow(f);
      setByCategory(spend.byCategory);
      setByMerchant(spend.byMerchant);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [range, depth]);

  useEffect(() => {
    void load();
  }, [load]);

  const spent = flow?.spentCents ?? 0;
  const income = flow?.incomeCents ?? 0;
  const net = income - spent;

  // Month pace: a straight-line extrapolation, labelled as such — no invented budget.
  let pace: number | null = null;
  if (rangeKey === 'month') {
    const now = new Date();
    const day = now.getDate();
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    if (day >= 3 && day < daysInMonth) pace = Math.round((spent / day) * daysInMonth);
  }
  const heroLabel =
    rangeKey === 'month' ? 'Spent this month' : `Spent · last ${rangeKey === '1y' ? 'year' : rangeKey.replace('d', ' days')}`;

  const biggest = byCategory[0]?.cents ?? 0;
  const shownCats = allCats ? byCategory : byCategory.slice(0, 8);
  const merchants = byMerchant.slice(0, allMerchants ? 10 : 5);
  // Detailed rows show where they sit ("Dining" under Lifestyle); top-level rows are the roots themselves.
  const catTitle = (c: string): string => (depth === '2' ? cats.leaf(c) : cats.full(c));
  const catParent = (c: string): string | null => (depth === '2' && c.includes('.') ? cats.leaf(c.split('.')[0]) : null);

  return (
    <>
      <div className="hb-seg hb-seg--inline mn-range">
        <Segmented label="Date range" value={rangeKey} options={RANGES} onChange={setRangeKey} />
      </div>

      {error && <ErrorState message={error} onRetry={() => void load()} />}

      {loading && !flow ? (
        <SkeletonGroup />
      ) : (
        flow && (
          <>
            <Card className="mn-hero" label={heroLabel}>
              <div className="hb-hero">
                <div className="hb-hero__text">
                  <span className="hb-hero__label">{heroLabel}</span>
                  <span className="hb-hero__value">{dollars(spent)}</span>
                  {pace !== null && (
                    <span className="hb-hero__sub">
                      On pace for <b className="num">{dollars(pace)}</b> this month
                    </span>
                  )}
                  <span className="hb-hero__sub">
                    <span className="num">{dollars(income)}</span> in ·{' '}
                    <span className={`num ${net >= 0 ? 'hb-amount--in' : 'hb-amount--warn'}`}>
                      {net >= 0 ? `${dollars(net)} saved` : `${dollars(-net)} over`}
                    </span>
                  </span>
                </div>
              </div>
            </Card>

            {(flow.incomeCents > 0 || flow.spentCents > 0) && (
              <section className="ui-groupwrap">
                <div className="ui-group__head">
                  <h2 className="ui-group__title">Cash flow</h2>
                </div>
                <div className="ui-card mn-flow">
                  <CashFlowSankey flow={flow} label={cats.leaf} onDrill={setDrill} />
                </div>
              </section>
            )}

            <div className="mn-where__head">
              <h2 className="ui-group__title">Where it went</h2>
              <div className="mn-where__depth">
                <Segmented label="Category detail" value={depth} options={DEPTHS} onChange={setDepth} />
              </div>
            </div>
            {byCategory.length === 0 ? (
              <EmptyState icon="check" title="No spending in this window" />
            ) : (
              <Group label="Where it went">
                {shownCats.map((row) => {
                  const parent = catParent(row.category);
                  return (
                    <Row
                      key={row.category}
                      className="mn-cat"
                      title={
                        <>
                          {isUncategorized(row.category) ? 'Uncategorized' : catTitle(row.category)}
                          {parent && <span className="mn-cat__parent"> · {parent}</span>}
                        </>
                      }
                      sub={
                        <span className="hb-bar__track mn-cat__track" aria-hidden="true">
                          <span
                            className="hb-bar__fill"
                            style={{ width: biggest > 0 ? `${Math.max(2, (row.cents / biggest) * 100)}%` : '0%' }}
                          />
                        </span>
                      }
                      ariaLabel={`${cats.full(row.category)}: ${money(row.cents)}, ${row.count} ${row.count === 1 ? 'charge' : 'charges'}`}
                      detail={<span className="hb-amount">{money(row.cents)}</span>}
                      onClick={() => setDrill({ title: cats.full(row.category), categories: [row.category] })}
                      chevron
                    />
                  );
                })}
                {!allCats && <MoreRow count={byCategory.length - shownCats.length} onClick={() => setAllCats(true)} />}
              </Group>
            )}

            {merchants.length > 0 && (
              <Group header="Top merchants">
                {merchants.map((m) => (
                  <Row
                    key={m.merchant}
                    title={m.merchant}
                    sub={`${m.count} ${m.count === 1 ? 'charge' : 'charges'}`}
                    detail={<span className="hb-amount">{money(m.cents)}</span>}
                  />
                ))}
                {!allMerchants && (
                  <MoreRow count={Math.min(10, byMerchant.length) - merchants.length} onClick={() => setAllMerchants(true)} />
                )}
              </Group>
            )}
          </>
        )
      )}

      {drill && (
        <CategoryDrill drill={drill} range={range} onClose={() => setDrill(null)} onChange={() => void load()} />
      )}
    </>
  );
}
